import { useState, useEffect, useCallback } from 'react';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/form';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/sheet';
import { zodResolver } from '@hookform/resolvers/zod';
import service from '@Service';
import { cn } from '@Utils/cn';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/select';
import type { PermissionRow } from './userRolePermissionList';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/popover';
import { ChevronsUpDown, Check, Edit, Plus, Loader2 } from 'lucide-react';

type SelectOption = {
  value: string;
  label: string;
};

const TargetSchema = z.object({
  value: z.string(),
  label: z.string(),
});

const CategorySchema = z.object({
  targetType: z.string(),
  targetIds: z.array(TargetSchema),
});

export type PermissionFormValues = z.infer<typeof CategorySchema>;

const DEFAULT_TARGET_TYPES = [
  'Tag',
  'Segment',
  'Fish',
  'Inbox',
  'Code',
  'Workspace',
];

function UserRolePermissionForm({
  edit = false,
  isEdit,
  data = null,
}: {
  edit?: boolean;
  isEdit: boolean;
  data?: PermissionRow | null;
}) {
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [targetOptions, setTargetOptions] = useState<SelectOption[]>([]);

  const form = useForm<PermissionFormValues>({
    resolver: zodResolver(CategorySchema),
    defaultValues: {
      targetType: '',
      targetIds: [],
    },
  });

  const { handleSubmit, control, reset, watch, setValue } = form;

  const selectedTargetType = watch('targetType');

  const fetchAllFish = async (search) => {
    try {
      if (!search) return;
      let query: any = {
        'include': 'fishProfiles',
        'include[fishProfiles][filter][deletedAt]': null,
      };

      if (search) {
        query = {
          'filter[0][phoneNumber]': `*${search}*`,
          'filter[1][fishProfiles][some][fName]': `*${search}*`,
          'filter[2][fishProfiles][some][lName]': `*${search}*`,
          'filter[3][fishProfiles][some][email]': `*${search}*`,
          ...query,
        };
      }

      const fish = await service.readAllFish(query);

      const options = fish.map((f) => {
        const profile = f.fishProfiles.find(
          (profile) => profile.profileType === 'MEMBER',
        );

        const name = profile
          ? `${profile.fName} ${profile.lName}`
          : `${f.fishProfiles[0].fName} ${f.fishProfiles[0].lName}`;

        const email = profile ? profile.email : f.fishProfiles[0].email;
        return {
          id: f.id,
          name,
          phoneNumber: f.phoneNumber,
          email,
        };
      });

      return options;
    } catch (error) {
      console.error('Failed to fetch fish:', error);
      return [];
    }
  };

  const searchAllTags = async (search) => {
    try {
      if (!search) return;
      const query = {
        'filter[name]': `*${search}*`,
      };

      const tags = await service.getAllTagsWithQuery(query);

      return tags;
    } catch (error) {
      console.error('Failed to fetch tags:', error);
      return [];
    }
  };

  const searchAllSegments = async (search) => {
    try {
      if (!search) return;
      const query = {
        'filter[name]': `*${search}*`,
      };

      const segments = await service.getAllSegmentsWithQuery(query);
      return segments;
    } catch (error) {
      console.error('Failed to fetch segments:', error);
      return [];
    }
  };

  const searchAllInboxes = async (search) => {
    try {
      if (!search) return;
      const query = {
        'filter[0][phoneNumber]': `*${search}*`,
        'filter[1][email]': `*${search}*`,
      };

      const inboxes = await service.getAllInboxes(query);
      return inboxes;
    } catch (error) {
      console.error('Failed to fetch inboxes:', error);
      return [];
    }
  };

  const searchAllCodes = async (search) => {
    try {
      if (!search) return;
      const query = {
        'filter[code]': `*${search}*`,
      };

      const codes = await service.getAllRegistrationCodes(query);
      return codes?.data;
    } catch (error) {
      console.error('Failed to fetch codes:', error);
      return [];
    }
  };

  const searchAllWorkspaces = async (search) => {
    try {
      if (!search) return;
      const query = {
        'filter[name]': `*${search}*`,
      };

      const workspaces = await service.getWorkspaces(query);
      return workspaces?.data;
    } catch (error) {
      console.error('Failed to fetch workspaces:', error);
      return [];
    }
  };

  const fetchOptions = useCallback(
    async (type: string, searchTerm: string): Promise<SelectOption[]> => {
      if (!type) return [];

      try {
        switch (type) {
          case 'Fish': {
            const res = await fetchAllFish(searchTerm);
            return (res ?? []).map((fish) => ({
              value: fish.id,
              label:
                `${fish.name || 'N/A'} ${fish.email || fish.phoneNumber ? `(${fish.email || fish.phoneNumber})` : ''}`.trim(),
            }));
          }
          case 'Tag': {
            const res = await searchAllTags(searchTerm);
            return (res ?? []).map((tag) => ({
              value: tag.id,
              label: tag.name,
            }));
          }
          case 'Segment': {
            const res = await searchAllSegments(searchTerm);
            return (res ?? []).map((s) => ({ value: s.id, label: s.name }));
          }
          case 'Inbox': {
            const res = await searchAllInboxes(searchTerm);
            return (res ?? []).map((inbox) => ({
              value: inbox.id,
              label: inbox.email ?? inbox.phoneNumber,
            }));
          }
          case 'Code': {
            const res: any = await searchAllCodes(searchTerm);
            return (res ?? []).map((code) => ({
              value: code.id,
              label: code.code,
            }));
          }
          case 'Workspace': {
            const res = await searchAllWorkspaces(searchTerm);
            return (res ?? []).map((ws) => ({ value: ws.id, label: ws.name }));
          }
          default:
            return [];
        }
      } catch {
        return [];
      }
    },
    [],
  );

  const onSubmit = async (values: PermissionFormValues) => {
    // update values in the row / parent list
    data.targetIds = values.targetIds;
    data.targetType = values.targetType;

    setSheetOpen(false);
  };

  const handleTargetTypeChange = (val: string) => {
    setValue('targetType', val);
    setValue('targetIds', []);
    setTargetOptions([]);
    setSearch('');
  };

  const hydrateConfig = async () => {
    if (!data?.targetIds?.length) return;

    const resolveLabel = async (targetType: string, targetId: string) => {
      switch (targetType) {
        case 'Workspace': {
          const workspace = await service.getWorkspace(targetId);
          return workspace.name;
        }
        case 'Tag': {
          const res = await service.readTagById(targetId);
          const tag = await res.json();
          return tag.name;
        }
        case 'Segment': {
          const segment = await service.readSegmentById(targetId);
          return segment.name;
        }
        case 'Fish': {
          const fish = await service.readFishById(targetId);
          return fish.name;
        }
        case 'Inbox': {
          const inbox = await service.readInbox(targetId);
          return inbox.email ?? inbox.phoneNumber ?? inbox.id;
        }
        case 'Code': {
          const code = await service.getRegistrationCodeById(targetId);
          return code.code;
        }
        default:
          return targetId;
      }
    };

    const targetIdsArray = await Promise.all(
      data.targetIds.map(async (targetId) => ({
        value: targetId.value,
        label: await resolveLabel(data.targetType, targetId.value),
      })),
    );

    setValue('targetType', data.targetType);
    setValue('targetIds', targetIdsArray);
  };

  useEffect(() => {
    if (!selectedTargetType) return;
    const timer = setTimeout(async () => {
      setIsLoading(true);
      const options = await fetchOptions(selectedTargetType, search);
      setTargetOptions(options);
      setIsLoading(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [search, selectedTargetType, fetchOptions]);

  useEffect(() => {
    if (!sheetOpen) {
      reset();
    } else {
      if (data?.targetIds?.length) {
        hydrateConfig();
      }
    }
  }, [sheetOpen, data]);

  return (
    <div onClick={(e) => e.stopPropagation()}>
      {data?.requiresTarget && (
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetTrigger asChild>
            <Button
              disabled={!isEdit}
              variant="outline"
              size="sm"
              icon={{
                src: edit ? <Edit /> : <Plus />,
              }}
              className={cn('justify-start')}
            >
              Configure
            </Button>
          </SheetTrigger>
          <SheetContent className="gap-0">
            <Form {...form}>
              <form
                onClick={(e) => e.stopPropagation()}
                onSubmit={handleSubmit(onSubmit)}
              >
                <SheetHeader>
                  <SheetTitle>Configure Permission</SheetTitle>
                </SheetHeader>
                <SheetBody>
                  <div className="max-w-sm space-y-2">
                    <FormField
                      name="targetType"
                      control={control}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Target Type</FormLabel>
                          <FormControl>
                            <Select
                              value={field.value}
                              onValueChange={(val) => {
                                handleTargetTypeChange(val);
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select a target type" />
                              </SelectTrigger>
                              <SelectContent>
                                {DEFAULT_TARGET_TYPES.map((type, i) => (
                                  <SelectItem key={`${i}-${type}`} value={type}>
                                    {type}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={control}
                      name="targetIds"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <FormLabel>Targets</FormLabel>

                          <Popover modal={true}>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button
                                  variant="outline"
                                  role="combobox"
                                  className={cn(
                                    'justify-between overflow-hidden',
                                    !field.value?.length &&
                                      'text-muted-foreground',
                                  )}
                                >
                                  <span className="truncate">
                                    {field.value?.length
                                      ? field.value
                                          .map((t) => t.label)
                                          .join(', ')
                                      : 'Select targets'}
                                  </span>

                                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                </Button>
                              </FormControl>
                            </PopoverTrigger>

                            <PopoverContent className="w-[300px] p-0">
                              <Command shouldFilter={false}>
                                <CommandInput
                                  placeholder="Search targets..."
                                  value={search}
                                  onValueChange={setSearch}
                                />

                                <CommandList className="h-[200px] overflow-y-auto">
                                  {isLoading ? (
                                    <div className="flex h-full items-center justify-center">
                                      <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
                                    </div>
                                  ) : (
                                    <>
                                      <CommandEmpty>
                                        No targets found.
                                      </CommandEmpty>
                                      <CommandGroup>
                                        {targetOptions?.map((target) => {
                                          const isSelected = field.value?.some(
                                            (t) => t.value === target.value,
                                          );

                                          return (
                                            <CommandItem
                                              key={target.value}
                                              onSelect={() => {
                                                if (isSelected) {
                                                  field.onChange(
                                                    field.value.filter(
                                                      (t) =>
                                                        t.value !==
                                                        target.value,
                                                    ),
                                                  );
                                                } else {
                                                  field.onChange([
                                                    ...(field.value || []),
                                                    target,
                                                  ]);
                                                }
                                              }}
                                            >
                                              <Check
                                                className={cn(
                                                  'mr-2 h-4 w-4',
                                                  isSelected
                                                    ? 'opacity-100'
                                                    : 'opacity-0',
                                                )}
                                              />

                                              {target.label}
                                            </CommandItem>
                                          );
                                        })}
                                      </CommandGroup>
                                    </>
                                  )}
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>

                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </SheetBody>
                <SheetFooter>
                  <Button type="submit">Set</Button>
                </SheetFooter>
              </form>
            </Form>
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
}

export default UserRolePermissionForm;
